import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import CommonCardHeader from "../../Utils/CommonComponents/CommonCardHeader";
import http from "../../services/http";

const VideosList = () => {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const API_BASE = process.env.REACT_APP_VIDEO_API;

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    const filtered = videos.filter(
      (video) =>
        video.prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.scene?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.mascotName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredVideos(filtered);
  }, [searchTerm, videos]);

  const fetchVideos = async () => {
    try {
      const response = await http.get(`${API_BASE}/videos`);
      setVideos(response.data.videos || []);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch videos");
      setLoading(false);
    }
  };

  const handleDownload = (videoUrl, videoId) => {
    const link = document.createElement("a");
    link.href = videoUrl;
    link.download = `video-${videoId}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (videoUrl) => {
    window.open(videoUrl, "_blank");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="page-body">
        <Container fluid>
          <div className="text-center mt-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading videos...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Generated Videos</h5>
                <div className="d-flex gap-2">
                  <Button
                    color="info"
                    href={`${process.env.PUBLIC_URL}/scenes`}
                  >
                    View Scenes
                  </Button>
                  <Button
                    color="primary"
                    href={`${process.env.PUBLIC_URL}/stories`}
                  >
                    Generate Story
                  </Button>
                </div>
              </div>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Row className="mb-3">
                  <Col md={6}>
                    <Input
                      type="text"
                      placeholder="Search by prompt, scene, or mascot name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md={6} className="text-end">
                    <Badge color="info">
                      {filteredVideos.length} video
                      {filteredVideos.length !== 1 ? "s" : ""} found
                    </Badge>
                  </Col>
                </Row>

                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Preview</th>
                      <th>Prompt</th>
                      <th>Scene</th>
                      <th>Duration</th>
                      <th>Cost</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVideos.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          {searchTerm
                            ? "No videos found matching your search."
                            : "No videos generated yet."}
                        </td>
                      </tr>
                    ) : (
                      filteredVideos.map((video, index) => (
                        <tr key={video.videoId}>
                          <td>{index + 1}</td>
                          <td>
                            <video
                              width="80"
                              height="60"
                              style={{
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              muted
                            >
                              <source src={video.videoUrl} type="video/mp4" />
                            </video>
                          </td>
                          <td>
                            <div style={{ maxWidth: "200px" }}>
                              <small>{video.prompt}</small>
                            </div>
                          </td>
                          <td>
                            <div style={{ maxWidth: "150px" }}>
                              <small>{video.scene}</small>
                            </div>
                          </td>

                          <td>{video.duration}s</td>
                          <td>
                            <Badge color="warning">{video.cost} credits</Badge>
                          </td>
                          <td>
                            <Badge
                              color={
                                video.status === "completed"
                                  ? "success"
                                  : "warning"
                              }
                              className="text-capitalize"
                            >
                              {video.status}
                            </Badge>
                          </td>
                          <td>
                            <small>{formatDate(video.createdAt)}</small>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleView(video.videoUrl)}
                                title="View Video"
                              >
                                <i className="fa fa-eye"></i>
                              </Button>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() =>
                                  handleDownload(video.videoUrl, video.videoId)
                                }
                                title="Download Video"
                              >
                                <i className="fa fa-download"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VideosList;
